import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { authFetch } from "../utils/authFetch";
import { initials } from "../utils/initials";
import { ExternalUserDetailModal } from "./ExternalUserDetail";
import { ConfirmDialog } from "./ConfirmDialog";
import PaginationBar from "./PaginationBar";
import { Users, ChevronRight, Plus, Mail, Trash2, Send, Search, Loader2 } from "lucide-react";

interface ExternalUser {
  id: string; email: string; name: string | null;
  avatarUrl: string | null; status: string;
  partnerMemberships: { partnerId: string; partnerName: string; partnerSlug: string }[];
  partnerCount: number; createdAt: string;
}
interface ExternalInvite {
  id: string; email: string; status: string;
  expiresAt: string; createdAt: string;
  partnerId: string | null; partnerName: string | null;
  /** Projects a Joveo user shared with this address (self-serve sharing). */
  sharedProjects?: string[];
}
interface PartnerOption { id: string; name: string }

export function ExternalUsersSection() {
  const [tab, setTab] = useState<"users" | "invites">("users");
  const [users, setUsers] = useState<ExternalUser[]>([]);
  const [invites, setInvites] = useState<ExternalInvite[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  const [userSearch, setUserSearch] = useState("");
  const [userTotal, setUserTotal] = useState(0);
  const [userPage, setUserPage] = useState(0);
  const [userPageSize, setUserPageSize] = useState(10);
  /** "" = all, "none" = in no partner, else a partner id. */
  const [userPartner, setUserPartner] = useState("");
  const [partners, setPartners] = useState<PartnerOption[]>([]);

  const [inviteSearch, setInviteSearch] = useState("");
  const [inviteTotal, setInviteTotal] = useState(0);
  const [invitePage, setInvitePage] = useState(0);
  const [invitePageSize, setInvitePageSize] = useState(10);

  const [modalUserId, setModalUserId] = useState<string | null>(null);
  const [modalInviteMode, setModalInviteMode] = useState(false);
  // ?open=<user or invite id> (the Change links on a person's or a project's
  // access answers) lands straight on that person's Doc Access.
  const [searchParams, setSearchParams] = useSearchParams();
  const [modalTab, setModalTab] = useState<"partners" | "access">("partners");
  // Ids are hex / tsid strings; anything else is not one of ours, and must
  // not reach the authenticated fetch path the modal builds from it.
  const rawOpen = searchParams.get("open");
  const openParam = rawOpen && /^[A-Za-z0-9_-]{1,64}$/.test(rawOpen) ? rawOpen : null;
  useEffect(() => {
    if (!rawOpen) return;
    if (!openParam) { setSearchParams(prev => { const n = new URLSearchParams(prev); n.delete("open"); return n; }, { replace: true }); return; }
    setModalUserId(openParam); setModalInviteMode(false); setModalTab("access");
    setSearchParams(prev => { const n = new URLSearchParams(prev); n.delete("open"); return n; }, { replace: true });
  }, [rawOpen, openParam, setSearchParams]);
  const showModal = !!modalUserId || modalInviteMode;
  const [confirmDlg, setConfirmDlg] = useState<{
    title: string; message: string; confirmLabel: string; onConfirm: () => void;
  } | null>(null);

  const userAbort = useRef<AbortController | null>(null);
  const inviteAbort = useRef<AbortController | null>(null);

  const loadUsers = useCallback(async (search: string, page: number, pageSize: number, partner: string) => {
    userAbort.current?.abort();
    const ctrl = new AbortController();
    userAbort.current = ctrl;
    const params = new URLSearchParams({ limit: String(pageSize), offset: String(page * pageSize) });
    if (search.trim()) params.set("search", search.trim());
    if (partner) params.set("partner_id", partner);
    try {
      const r = await authFetch(`/api/admin/external-users?${params}`, { signal: ctrl.signal });
      if (ctrl.signal.aborted) return;
      if (r.ok) {
        const data = await r.json();
        if (Array.isArray(data)) {
          setUsers(data.filter((u: ExternalUser) => u.status !== "invited"));
          setUserTotal(data.length);
        } else {
          setUsers((data.users || []).filter((u: ExternalUser) => u.status !== "invited"));
          setUserTotal(data.total ?? 0);
        }
      }
    } catch (e: unknown) { if (e instanceof DOMException && e.name === "AbortError") return; }
    if (!ctrl.signal.aborted) setSearching(false);
  }, []);

  const loadInvites = useCallback(async (search: string, page: number, pageSize: number) => {
    inviteAbort.current?.abort();
    const ctrl = new AbortController();
    inviteAbort.current = ctrl;
    const params = new URLSearchParams({ limit: String(pageSize), offset: String(page * pageSize) });
    if (search.trim()) params.set("search", search.trim());
    try {
      const r = await authFetch(`/api/admin/external-users/invites/list?${params}`, { signal: ctrl.signal });
      if (ctrl.signal.aborted) return;
      if (r.ok) {
        const data = await r.json();
        setInvites(Array.isArray(data) ? data : (data.invites || []));
        setInviteTotal(data.total ?? (Array.isArray(data) ? data.length : 0));
      }
    } catch (e: unknown) { if (e instanceof DOMException && e.name === "AbortError") return; }
    if (!ctrl.signal.aborted) setSearching(false);
  }, []);

  // Initial load
  useEffect(() => {
    Promise.all([loadUsers("", 0, 10, ""), loadInvites("", 0, 10)]).then(() => setInitialLoading(false));
  }, [loadUsers, loadInvites]);

  useEffect(() => {
    authFetch("/api/admin/partners")
      .then(r => (r.ok ? r.json() : []))
      .then(setPartners)
      .catch(() => {});
  }, []);

  const closeModal = () => { setModalUserId(null); setModalInviteMode(false); loadUsers(userSearch, userPage, userPageSize, userPartner); loadInvites(inviteSearch, invitePage, invitePageSize); };

  // Debounced search — waits 500ms after user stops typing, then fires one request
  const userSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleUserSearch = (v: string) => {
    setUserSearch(v);
    setSearching(true);
    if (userSearchTimer.current) clearTimeout(userSearchTimer.current);
    userSearchTimer.current = setTimeout(() => { setUserPage(0); loadUsers(v, 0, userPageSize, userPartner); }, 500);
  };
  const handlePartnerChange = (v: string) => {
    if (userSearchTimer.current) clearTimeout(userSearchTimer.current);
    setUserPartner(v); setUserPage(0); loadUsers(userSearch, 0, userPageSize, v);
  };
  const inviteSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleInviteSearch = (v: string) => {
    setInviteSearch(v);
    setSearching(true);
    if (inviteSearchTimer.current) clearTimeout(inviteSearchTimer.current);
    inviteSearchTimer.current = setTimeout(() => { setInvitePage(0); loadInvites(v, 0, invitePageSize); }, 500);
  };

  const currentSearch = tab === "users" ? userSearch : inviteSearch;
  const handleSearch = tab === "users" ? handleUserSearch : handleInviteSearch;
  const isLoading = tab === "users" ? initialLoading : initialLoading;

  const deleteUser = (e: React.MouseEvent, userId: string, label: string) => {
    e.stopPropagation();
    setConfirmDlg({
      title: "Delete user",
      message: `Delete user "${label}"? This will remove all memberships and access config.`,
      confirmLabel: "Delete",
      onConfirm: async () => {
        setConfirmDlg(null);
        const r = await authFetch(`/api/admin/external-users/${userId}`, { method: "DELETE" });
        if (r.ok) { loadUsers(userSearch, userPage, userPageSize, userPartner); loadInvites(inviteSearch, invitePage, invitePageSize); }
        else { const d = await r.json().catch(() => ({})); setConfirmDlg({ title: "Error", message: d.detail || "Failed to delete", confirmLabel: "OK", onConfirm: () => setConfirmDlg(null) }); }
      },
    });
  };
  const deleteInvite = (e: React.MouseEvent, inviteId: string, email: string, shared: string[] = []) => {
    e.stopPropagation();
    setConfirmDlg({
      title: "Delete invite",
      // A share invite's projects go with it — say so rather than revoke a
      // colleague's share silently.
      message: shared.length
        ? `Delete invite for "${email}"? It was sent when ${shared.length === 1 ? `"${shared[0]}" was` : `${shared.length} projects (${shared.join(", ")}) were`} shared with them, and they lose ${shared.length === 1 ? "that share" : "those shares"}.`
        : `Delete invite for "${email}"?`,
      confirmLabel: "Delete",
      onConfirm: async () => {
        setConfirmDlg(null);
        const r = await authFetch(`/api/admin/external-users/${inviteId}`, { method: "DELETE" });
        if (r.ok) loadInvites(inviteSearch, invitePage, invitePageSize);
        else { const d = await r.json().catch(() => ({})); setConfirmDlg({ title: "Error", message: d.detail || "Failed to delete", confirmLabel: "OK", onConfirm: () => setConfirmDlg(null) }); }
      },
    });
  };
  const resendInvite = async (e: React.MouseEvent, inviteId: string, email: string) => {
    e.stopPropagation();
    const r = await authFetch(`/api/admin/external-users/invites/${inviteId}/resend`, { method: "POST" });
    if (r.ok) {
      loadInvites(inviteSearch, invitePage, invitePageSize);
      setConfirmDlg({ title: "Invite resent", message: `A new invite email has been sent to ${email}.`, confirmLabel: "OK", onConfirm: () => setConfirmDlg(null) });
    } else {
      const d = await r.json().catch(() => ({}));
      setConfirmDlg({ title: "Error", message: d.detail || "Failed to resend invite", confirmLabel: "OK", onConfirm: () => setConfirmDlg(null) });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-base font-semibold text-slate-800">External Users</h1>
          <span className="text-[11px] font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md tabular-nums">{tab === "users" ? userTotal : inviteTotal}</span>
        </div>
        <button onClick={() => setModalInviteMode(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors">
          <Plus className="size-3.5" /> Invite User
        </button>
      </div>
      <div className="flex items-center justify-between gap-3">
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          <button onClick={() => setTab("users")} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === "users" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            Users {userTotal > 0 && <span className="text-slate-400 ml-1">{userTotal}</span>}
          </button>
          <button onClick={() => setTab("invites")} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${tab === "invites" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            Invites {inviteTotal > 0 && <span className="text-slate-400 ml-1">{inviteTotal}</span>}
          </button>
        </div>
        <div className="flex items-center gap-2">
        {tab === "users" && (
          <select value={userPartner} onChange={e => handlePartnerChange(e.target.value)} aria-label="Filter by partner"
            className="max-w-48 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5 outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400">
            <option value="">All partners</option>
            <option value="none">No partner</option>
            {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        )}
        <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 w-64 focus-within:ring-1 focus-within:ring-indigo-400 focus-within:border-indigo-400 focus-within:bg-white transition-colors">
          {searching ? <Loader2 className="size-3 text-indigo-400 shrink-0 animate-spin" /> : <Search className="size-3 text-slate-400 shrink-0" />}
          <input value={currentSearch} onChange={e => handleSearch(e.target.value)} placeholder="Search…" className="flex-1 text-xs text-slate-700 placeholder:text-slate-400 bg-transparent outline-none" />
        </div>
        </div>
      </div>

      {tab === "users" && (
        isLoading ? <div className="text-sm text-slate-400 py-8 text-center">Loading…</div> : users.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-200 rounded-xl px-4 py-10 text-center">
            <Users className="size-7 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-400">{userSearch || userPartner ? "No users match your filters." : "No external users yet."}</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {users.map(u => (
              <div key={u.id} onClick={() => { setModalUserId(u.id); setModalInviteMode(false); setModalTab("partners"); }}
                className="w-full bg-white border border-slate-200 rounded-xl flex items-center gap-3 px-4 py-3 hover:border-slate-300 transition-colors text-left cursor-pointer">
                <div className="size-8 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center text-[11px] font-semibold shrink-0">{initials(u.name, u.email)}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{u.name || u.email}</div>
                  <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-2">
                    {u.name && <span className="truncate">{u.email}</span>}
                    {u.partnerCount > 0 && <span>{u.partnerCount} partner{u.partnerCount !== 1 ? "s" : ""}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${u.status === "active" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>{u.status}</span>
                  <button onClick={(e) => deleteUser(e, u.id, u.name || u.email)} className="p-1 text-slate-300 hover:text-red-500 transition-colors" title="Delete user"><Trash2 className="size-3.5" /></button>
                  <ChevronRight className="size-4 text-slate-300" />
                </div>
              </div>
            ))}
          </div>
        )
      )}
      {tab === "users" && <PaginationBar total={userTotal} page={userPage} pageSize={userPageSize} onPageChange={(p) => { setUserPage(p); loadUsers(userSearch, p, userPageSize, userPartner); }} onPageSizeChange={(s) => { if (userSearchTimer.current) clearTimeout(userSearchTimer.current); setUserPageSize(s); setUserPage(0); loadUsers(userSearch, 0, s, userPartner); }} />}

      {tab === "invites" && (
        isLoading ? <div className="text-sm text-slate-400 py-8 text-center">Loading…</div> : invites.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-200 rounded-xl px-4 py-10 text-center">
            <Mail className="size-7 text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-400">{inviteSearch ? "No invites match your search." : "No invites yet."}</p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {invites.map(inv => (
              <div key={inv.id} onClick={() => { setModalUserId(inv.id); setModalInviteMode(false); setModalTab("partners"); }}
                className="w-full bg-white border border-slate-200 rounded-xl flex items-center gap-3 px-4 py-3 hover:border-slate-300 transition-colors text-left cursor-pointer">
                <div className="size-8 rounded-full bg-slate-200 flex items-center justify-center text-[11px] font-semibold text-slate-500 shrink-0">{inv.email[0].toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{inv.email}</div>
                  <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-2">
                    <span>Sent {new Date(inv.createdAt).toLocaleDateString()}</span>
                    {inv.partnerName && <span>{"\u00b7"} {inv.partnerName}</span>}
                    {!!inv.sharedProjects?.length && (
                      <span className="truncate" title={inv.sharedProjects.join(", ")}>
                        {"\u00b7"} Shared: {inv.sharedProjects.join(", ")}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${inv.status === "pending" ? "bg-amber-50 text-amber-600" : inv.status === "accepted" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{inv.status}</span>
                  {inv.status === "pending" && (
                    <button onClick={(e) => resendInvite(e, inv.id, inv.email)} className="p-1 text-slate-300 hover:text-indigo-500 transition-colors" title="Resend invite"><Send className="size-3.5" /></button>
                  )}
                  <button onClick={(e) => deleteInvite(e, inv.id, inv.email, inv.sharedProjects)} className="p-1 text-slate-300 hover:text-red-500 transition-colors" title="Delete invite"><Trash2 className="size-3.5" /></button>
                  <ChevronRight className="size-4 text-slate-300" />
                </div>
              </div>
            ))}
          </div>
        )
      )}
      {tab === "invites" && <PaginationBar total={inviteTotal} page={invitePage} pageSize={invitePageSize} onPageChange={(p) => { setInvitePage(p); loadInvites(inviteSearch, p, invitePageSize); }} onPageSizeChange={(s) => { if (inviteSearchTimer.current) clearTimeout(inviteSearchTimer.current); setInvitePageSize(s); setInvitePage(0); loadInvites(inviteSearch, 0, s); }} />}

      {showModal && (
        <ExternalUserDetailModal key={`${modalUserId}:${modalTab}`} userId={modalUserId} inviteMode={modalInviteMode} initialTab={modalTab} onClose={closeModal} />
      )}
      {confirmDlg && (
        <ConfirmDialog title={confirmDlg.title} message={confirmDlg.message}
          confirmLabel={confirmDlg.confirmLabel} destructive
          onConfirm={confirmDlg.onConfirm} onCancel={() => setConfirmDlg(null)} />
      )}
    </div>
  );
}
